/**
 * Export Lexical EditorState to Telegram MessageEntity format.
 *
 * This is the primary export format used by Telegram Post Editor.
 * Offsets and lengths are measured in UTF-16 code units, matching
 * Telegram's requirement and JavaScript's native string indexing.
 */
import type { EditorState } from 'lexical';
import type { MessageEntity, MessageEntityType } from '@tgpe/shared';
import {
  walkTree,
  isInCodeBlock,
  type ElementContext,
  type InlineFormat,
} from './tree-walker';

/**
 * Tracks an element-level entity that is currently open (not yet closed).
 */
interface OpenEntity {
  type: MessageEntityType;
  startOffset: number;
  extra?: {
    url?: string;
    language?: string;
    custom_emoji_id?: string;
    userId?: number;
  };
}

/**
 * Maps element context type to Telegram entity type.
 */
function mapElementType(
  type: ElementContext['type']
): MessageEntityType | null {
  switch (type) {
    case 'spoiler':
      return 'spoiler';
    case 'blockquote':
      return 'blockquote';
    case 'expandable_blockquote':
      return 'expandable_blockquote';
    case 'code_block':
      return 'pre';
    case 'link':
      return 'text_link';
    case 'custom_emoji':
      return 'custom_emoji';
    default:
      return null;
  }
}

/**
 * Extracts extra fields from an element context for the entity.
 */
function extractExtra(
  context: ElementContext
): OpenEntity['extra'] | undefined {
  switch (context.type) {
    case 'code_block':
      return context.language ? { language: context.language } : undefined;
    case 'link':
      if (context.userId !== undefined) {
        return { userId: context.userId };
      }
      return context.url ? { url: context.url } : undefined;
    case 'custom_emoji':
      return context.customEmojiId
        ? { custom_emoji_id: context.customEmojiId }
        : undefined;
    default:
      return undefined;
  }
}

/**
 * Creates a MessageEntity from open entity data.
 */
function createEntity(
  open: OpenEntity,
  endOffset: number
): MessageEntity | null {
  const length = endOffset - open.startOffset;
  if (length <= 0) return null;

  const base = {
    type: open.type,
    offset: open.startOffset,
    length,
  };

  // Add type-specific extra fields
  if (open.extra?.url && open.type === 'text_link') {
    return { ...base, url: open.extra.url } as MessageEntity;
  }
  if (open.extra?.userId && open.type === 'text_link') {
    // text_mention for tg://user links
    return {
      type: 'text_mention',
      offset: open.startOffset,
      length,
      user: {
        id: open.extra.userId,
        is_bot: false,
        first_name: '',
      },
    } as MessageEntity;
  }
  if (open.extra?.language && open.type === 'pre') {
    return { ...base, language: open.extra.language } as MessageEntity;
  }
  if (open.extra?.custom_emoji_id && open.type === 'custom_emoji') {
    return {
      ...base,
      custom_emoji_id: open.extra.custom_emoji_id,
    } as MessageEntity;
  }

  return base as MessageEntity;
}

/**
 * Checks if two entities have matching extra properties.
 */
function extrasMatch(a: MessageEntity, b: MessageEntity): boolean {
  if (a.type === 'text_link' && b.type === 'text_link') {
    return a.url === b.url;
  }
  if (a.type === 'pre' && b.type === 'pre') {
    return a.language === b.language;
  }
  if (a.type === 'custom_emoji' && b.type === 'custom_emoji') {
    return a.custom_emoji_id === b.custom_emoji_id;
  }
  if (a.type === 'text_mention' && b.type === 'text_mention') {
    return a.user.id === b.user.id;
  }
  return true;
}

/**
 * Merges adjacent entities of the same type.
 */
function mergeEntities(entities: MessageEntity[]): MessageEntity[] {
  if (entities.length === 0) return [];

  // Sort by type, then by offset
  const sorted = [...entities].sort((a, b) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return a.offset - b.offset;
  });

  const merged: MessageEntity[] = [];
  for (const entity of sorted) {
    const last = merged[merged.length - 1];
    if (
      last &&
      last.type === entity.type &&
      last.offset + last.length === entity.offset &&
      extrasMatch(last, entity)
    ) {
      // Extend the previous entity
      const extended = { ...last, length: last.length + entity.length };
      merged[merged.length - 1] = extended as MessageEntity;
    } else {
      merged.push(entity);
    }
  }

  return merged;
}

/**
 * Maps inline format to Telegram entity type.
 */
function mapInlineFormat(format: InlineFormat): MessageEntityType {
  return format as MessageEntityType;
}

/**
 * Convert a Lexical EditorState to a plain text string and a Telegram
 * MessageEntity array. This is the primary export format.
 *
 * Offsets and lengths in the returned entities are measured in UTF-16
 * code units, matching Telegram's requirement and JavaScript's native
 * string indexing.
 *
 * @param editorState - The Lexical editor state to export. Must be
 *   obtained from `editor.getEditorState()` or from a serialized state.
 * @returns An object with `text` (plain text with newlines) and
 *   `entities` (array of MessageEntity objects, sorted by offset).
 */
export function exportToEntities(editorState: EditorState): {
  text: string;
  entities: MessageEntity[];
} {
  const segments = walkTree(editorState);

  let plainText = '';
  const entities: MessageEntity[] = [];
  let currentOffset = 0;

  // Track open element-level entities
  const openEntities = new Map<string, OpenEntity>();
  let previousContext: ElementContext[] = [];

  for (const segment of segments) {
    const segmentStart = currentOffset;

    // Append text
    plainText += segment.text;
    currentOffset += segment.text.length; // UTF-16 code units

    const segmentEnd = currentOffset;
    const segmentLength = segmentEnd - segmentStart;

    if (segmentLength === 0) continue;

    const isCodeBlock = isInCodeBlock(segment);

    // Handle element context transitions
    const currentContext = segment.elementContext;

    // Find contexts that closed (were in previous but not in current)
    for (const prev of previousContext) {
      const stillOpen = currentContext.some((c) => c.key === prev.key);
      if (!stillOpen) {
        const open = openEntities.get(prev.key);
        if (open) {
          const entity = createEntity(open, segmentStart);
          if (entity) {
            entities.push(entity);
          }
          openEntities.delete(prev.key);
        }
      }
    }

    // Find contexts that opened (in current but not in previous)
    for (const curr of currentContext) {
      const wasOpen = previousContext.some((p) => p.key === curr.key);
      if (!wasOpen) {
        const entityType = mapElementType(curr.type);
        if (entityType) {
          openEntities.set(curr.key, {
            type: entityType,
            startOffset: segmentStart,
            extra: extractExtra(curr),
          });
        }
      }
    }

    previousContext = currentContext;

    // Skip inline formats if inside a code block
    if (isCodeBlock || segment.isSeparator) {
      continue;
    }

    // Emit inline format entities for this segment
    for (const format of segment.formats) {
      entities.push({
        type: mapInlineFormat(format),
        offset: segmentStart,
        length: segmentLength,
      } as MessageEntity);
    }
  }

  // Close any remaining open entities
  for (const [, open] of openEntities) {
    const entity = createEntity(open, currentOffset);
    if (entity) {
      entities.push(entity);
    }
  }
  openEntities.clear();

  // Merge adjacent entities of the same type
  const mergedEntities = mergeEntities(entities);

  // Sort by offset (ascending), then by length (descending for outer-first)
  mergedEntities.sort((a, b) => {
    if (a.offset !== b.offset) return a.offset - b.offset;
    return b.length - a.length;
  });

  // Trim trailing newlines and adjust entities
  let trimmedText = plainText;
  while (trimmedText.endsWith('\n')) {
    trimmedText = trimmedText.slice(0, -1);
  }

  // Adjust entity lengths if they extend past the trimmed text
  const finalEntities = mergedEntities
    .map((entity) => {
      const end = entity.offset + entity.length;
      if (end > trimmedText.length) {
        const newLength = trimmedText.length - entity.offset;
        if (newLength <= 0) return null;
        return { ...entity, length: newLength } as MessageEntity;
      }
      return entity;
    })
    .filter((e): e is MessageEntity => e !== null);

  return {
    text: trimmedText,
    entities: finalEntities,
  };
}
